import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { z } from "zod";
import { getPaymentSettingsByYear } from "../../../api/paiement_settings";
import { getBankAccountsByPaymentSettingId } from "../../../api/bank_account";
import { toast } from "sonner";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { updatePayment } from "../../../api/studentPaiement";
import { getStudentDiscountByYear } from "../../../api/studentPaiement";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

type Props = {
  show: boolean;
  onClose: () => void;
  selectedPlanIds: string[];
  id_studentProfil: string;
  allPlans: any[];
  payment?: any;
  onSave?: () => void;
  currency?: string;
};

enum PaymentType {
  ESPECE = "Espèce",
  CHEQUE = "Chèque",
  VIREMENT = "Virement",
}

const schema = z.object({
  amountReceived: z.number({ invalid_type_error: "Montant requis" }).positive(),
  refId: z.string().nonempty("Choissisez un compte bancaire valide"),
  type: z.enum([PaymentType.ESPECE, PaymentType.CHEQUE, PaymentType.VIREMENT]),
  receptionDate: z.string().nonempty("Date de reception requise"),
  description: z.string().optional(),
  allocations: z
    .array(
      z.object({
        planId: z.string(),
        amount: z.number().min(0),
      }),
    )
    .min(1),
});

const EditPaymentModal = ({
  show,
  onClose,
  id_studentProfil,
  selectedPlanIds,
  allPlans,
  payment,
  onSave,
  currency = "FCFA",
}: Props) => {
  const getTodayInputValue = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const { selectedYear } = useAcademicYear();
  const [discountMaps, setDiscountMaps] = useState<{
    byStudentPlanId: Record<string, number>;
    byPaymentPlanId: Record<string, number>;
  }>({ byStudentPlanId: {}, byPaymentPlanId: {} });

  const getPlanAmount = (plan: any) =>
    Number(plan?.PaymentPlan?.amount ?? plan?.amount ?? 0);

  const getPlanTemplateId = (plan: any) =>
    String(plan?.id_paymentPlans || plan?.PaymentPlan?.id || "");

  const getDiscountAmountByPlan = (plan: any) => {
    const studentPlanId = String(plan?.id || "");
    const templatePlanId = getPlanTemplateId(plan);
    const byStudent = Number(discountMaps.byStudentPlanId[studentPlanId] || 0);
    const byTemplate = Number(
      discountMaps.byPaymentPlanId[templatePlanId] || 0,
    );
    return byStudent > 0 ? byStudent : byTemplate;
  };

  const initialPaymentAllocationsByPlan = useMemo(() => {
    const repartitions = Array.isArray(payment?.repartitions)
      ? payment.repartitions
      : [];
    return repartitions.reduce(
      (acc: Record<string, number>, repartition: any) => {
        const planId = repartition?.payment_plan?.id;
        if (!planId) return acc;
        acc[planId] = (acc[planId] || 0) + Number(repartition?.amount || 0);
        return acc;
      },
      {},
    );
  }, [payment]);

  const getEditablePlanRemaining = (planId: string) => {
    const plan = allPlans.find((p: any) => p.id === planId);
    if (!plan) return 0;
    const grossAmount = getPlanAmount(plan);
    const discountAmount = getDiscountAmountByPlan(plan);
    const normalRemaining = Math.max(
      0,
      grossAmount - discountAmount - Number(plan.paid || 0),
    );
    const initialAllocated = initialPaymentAllocationsByPlan[planId] || 0;
    return normalRemaining + initialAllocated;
  };

  const [currentSelected, setCurrentSelected] =
    useState<string[]>(selectedPlanIds);
  const [amountReceived, setAmountReceived] = useState<number>(0);
  const [refId, setRefId] = useState<string>("");
  const [paymentType, setPaymentType] = useState<PaymentType>(
    PaymentType.ESPECE,
  );
  const [receptionDate, setReceptionDate] =
    useState<string>(getTodayInputValue());
  const [description, setDescription] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [allocations, setAllocations] = useState<
    { planId: string; amount: number }[]
  >([]);

  const [paymentSettings, setPaymentSettings] = useState<any>();
  const [bankAccounts, setBankAccounts] = useState<any>([]);

  const fetchStudentDiscounts = async () => {
    try {
      if (!selectedYear?.id || !id_studentProfil) {
        setDiscountMaps({ byStudentPlanId: {}, byPaymentPlanId: {} });
        return;
      }

      const res = await getStudentDiscountByYear(
        id_studentProfil,
        selectedYear.id,
        buildPermissionContext(Resource.PAYMENT, Action.UPDATE),
      );
      const byStudentPlanId: Record<string, number> = {};
      const byPaymentPlanId: Record<string, number> = {};

      const discounts = res?.data?.discount || [];
      discounts.forEach((discount: any) => {
        const repartitions = discount?.RepartitionStudentDiscounts || [];
        repartitions.forEach((rep: any) => {
          const repAmount = Number(rep?.amount || 0);
          const studentPlanId = String(rep?.id_studentPayementPlans || "");
          const paymentPlanId = String(
            rep?.StudentPayementPlan?.PaymentPlan?.id || "",
          );

          if (studentPlanId) {
            byStudentPlanId[studentPlanId] =
              Number(byStudentPlanId[studentPlanId] || 0) + repAmount;
          }
          if (paymentPlanId) {
            byPaymentPlanId[paymentPlanId] =
              Number(byPaymentPlanId[paymentPlanId] || 0) + repAmount;
          }
        });
      });

      setDiscountMaps({ byStudentPlanId, byPaymentPlanId });
    } catch (error: any) {
      setDiscountMaps({ byStudentPlanId: {}, byPaymentPlanId: {} });
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Impossible de charger les bourses/remises de l'etudiant",
      );
    }
  };

  const fetchPaymentParameters = async () => {
    try {
      if (!selectedYear) return;
      const res = await getPaymentSettingsByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.PAYMENT, Action.UPDATE),
      );
      setPaymentSettings(res?.data[0]);
    } catch (error: any) {
      // console.log('Error fetching payment parameters:', error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, RÃ©essayer plus tard",
      );
    }
  };

  const fetchBankAccountsByPaymentSettingId = async (
    id_paymentSetting: any,
  ) => {
    try {
      if (!id_paymentSetting) return;
      setBankAccounts([]);
      const res = await getBankAccountsByPaymentSettingId(
        id_paymentSetting,
        buildPermissionContext(Resource.PAYMENT, Action.UPDATE),
      );
      setBankAccounts(res?.data || []);
    } catch (error: any) {
      // console.log('Error fetching bank accounts:', error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, RÃ©essayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchPaymentParameters();
  }, [selectedYear]);

  useEffect(() => {
    if (!show) return;
    fetchStudentDiscounts();
  }, [show, selectedYear?.id, id_studentProfil]);

  useEffect(() => {
    if (!paymentSettings) return;
    fetchBankAccountsByPaymentSettingId(paymentSettings?.id);
  }, [paymentSettings]);

  useEffect(() => {
    if (!show || !payment) return;
    const paymentRepartitions = Array.isArray(payment?.repartitions)
      ? payment.repartitions
      : [];
    const planIds = paymentRepartitions
      .map((repartition: any) => repartition?.payment_plan?.id)
      .filter(Boolean);

    setCurrentSelected(planIds.length ? planIds : selectedPlanIds);
    setAmountReceived(
      paymentRepartitions.reduce(
        (sum: number, repartition: any) =>
          sum + Number(repartition?.amount || 0),
        0,
      ),
    );
    setRefId(payment?.bank_account?.id || "");
    setPaymentType(
      Object.values(PaymentType).includes(payment?.type)
        ? payment.type
        : PaymentType.ESPECE,
    );
    setReceptionDate(
      toDateInputValue(payment?.reception_date) || getTodayInputValue(),
    );
    setDescription(payment?.description || "");
    setAllocations(
      paymentRepartitions
        .map((repartition: any) => ({
          planId: repartition?.payment_plan?.id,
          amount: Number(repartition?.amount || 0),
        }))
        .filter((allocation: any) => allocation.planId),
    );
    setErrors({});
  }, [show, payment, selectedPlanIds]);

  useEffect(() => {
    if (!show) return;
    if (!bankAccounts.length) {
      setRefId("");
      return;
    }
    setRefId((prev) => {
      if (prev && bankAccounts.some((account: any) => account.id === prev)) {
        return prev;
      }
      return bankAccounts[0].id;
    });
  }, [bankAccounts, show]);

  const totalAllocated = useMemo(
    () => allocations.reduce((s, a) => s + (a.amount || 0), 0),
    [allocations],
  );

  const remaining = useMemo(
    () => Math.max(0, amountReceived - totalAllocated),
    [amountReceived, totalAllocated],
  );

  const parseDate = (d: string) => {
    const parts = d.split(".");
    if (parts.length !== 3) return new Date();
    return new Date(
      parseInt(parts[2]),
      parseInt(parts[1]) - 1,
      parseInt(parts[0]),
    );
  };

  const autoDistribute = () => {
    let rest = amountReceived;
    const sorted = [...currentSelected]
      .map((id) => allPlans.find((p: any) => p?.id === id)!)
      .filter(Boolean)
      .sort(
        (a, b) =>
          parseDate(a.PaymentPlan.due_date).getTime() -
          parseDate(b.PaymentPlan.due_date).getTime(),
      );
    const next: { planId: string; amount: number }[] = [];
    for (const p of sorted) {
      const remain = getEditablePlanRemaining(p?.id);
      const assign = Math.min(remain, rest);
      next.push({ planId: p?.id, amount: assign });
      rest -= assign;
      if (rest <= 0) break;
    }
    setAllocations(next);
  };

  const removePlan = (planId: string) => {
    setCurrentSelected((prev) => prev.filter((p) => p !== planId));
    setAllocations((prev) => prev.filter((a) => a.planId !== planId));
  };

  const setAllocation = (planId: string, value: number) => {
    const plan = allPlans.find((p: any) => p.id === planId);
    if (!plan) return;
    const max = getEditablePlanRemaining(planId);
    const v = Math.max(0, Math.min(value, max));
    setAllocations((prev) => {
      const idx = prev.findIndex((a) => a.planId === planId);
      if (idx >= 0) {
        const cp = [...prev];
        cp[idx] = { planId, amount: v };
        return cp;
      }
      return [...prev, { planId, amount: v }];
    });
  };

  const validateAndSubmit = () => {
    const base = {
      amountReceived,
      refId: refId || undefined,
      type: paymentType,
      receptionDate,
      description: description || undefined,
      allocations,
    };

    const planLimitsOk = allocations.every((a) => {
      const remain = getEditablePlanRemaining(a.planId);
      return a.amount <= remain;
    });
    const totalOk =
      allocations.reduce((s, a) => s + a.amount, 0) <= amountReceived;
    const parse = schema.safeParse(base);
    const nextErrors: Record<string, string> = {};
    if (!parse.success) {
      parse.error.issues.forEach((i) => {
        const k = i.path.join(".");
        nextErrors[k] = i.message;
      });
    }
    if (!planLimitsOk)
      nextErrors["allocations"] = "Montant supérieur au restant d'un plan";
    if (!totalOk)
      nextErrors["total"] = "Somme des plans dépasse le montant perçu";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      onSubmit(base);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      if (!payment?.payment_id) return;
      const payload = {
        id_payment: payment.payment_id,
        id_studentProfil,
        id_bankAccount: data.refId,
        type: data.type,
        description: data.description,
        amount_total: data.amountReceived,
        reception_date: data.receptionDate
          ? new Date(data.receptionDate)
          : payment?.reception_date || new Date(),
        selectedPlans: data.allocations.map((alloc: any) => ({
          id: alloc.planId,
          amount: alloc.amount,
        })),
      };
      // console.log('Payload: ', payload)
      await updatePayment(
        payment.payment_id,
        payload,
        buildPermissionContext(Resource.PAYMENT, Action.UPDATE),
      );
      toast.success("Payement modifié avec succès !");
      if (onSave) onSave();
      onClose();
    } catch (error: any) {
      // console.log('Erreur lors de la modification du paiement: ', error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fieldCardClass = "card shadow-sm border-dark rounded h-100";
  const fieldHeaderClass = "mb-2";
  const fieldBodyClass = "";
  const inputClass = "form-control";
  const selectClass = "form-select";

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title>Modifier un paiement</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-white">
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <div className={fieldCardClass}>
              <div className={fieldHeaderClass}>
                <h6 className="mb-0">Montant perçu</h6>
              </div>
              <div className={fieldBodyClass}>
                <div className="input-group">
                  <input
                    type="number"
                    className={inputClass}
                    value={amountReceived}
                    onChange={(e) =>
                      setAmountReceived(parseFloat(e.target.value || "0"))
                    }
                    min={0}
                  />
                  <span className="input-group-text ">{currency}</span>
                </div>
                {errors["amountReceived"] && (
                  <div className="text-danger small mt-1">
                    {errors["amountReceived"]}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className={fieldCardClass}>
              <div className={fieldHeaderClass}>
                <h6 className="mb-0">Compte bancaire</h6>
              </div>
              <div className={fieldBodyClass}>
                <select
                  className={selectClass}
                  value={refId}
                  onChange={(e) => setRefId(e.target.value)}
                  disabled={!bankAccounts.length}
                >
                  {!bankAccounts.length && (
                    <option value="">Aucun compte bancaire disponible</option>
                  )}
                  {bankAccounts.map((account: any) => (
                    <option key={account.id} value={account.id}>
                      {account.bank_name} - {account.account_title} (
                      {account.account_number})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <div className={fieldCardClass}>
              <div className={fieldHeaderClass}>
                <h6 className="mb-0">Type de paiement</h6>
              </div>
              <div className={fieldBodyClass}>
                <select
                  className={selectClass}
                  value={paymentType}
                  onChange={(e) =>
                    setPaymentType(e.target.value as PaymentType)
                  }
                >
                  {Object.values(PaymentType).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors["type"] && (
                  <div className="text-danger small mt-1">{errors["type"]}</div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className={fieldCardClass}>
              <div className={fieldHeaderClass}>
                <h6 className="mb-0">Description (optionnelle)</h6>
              </div>
              <div className={fieldBodyClass}>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Ajouter une note"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <div className={fieldCardClass}>
              <div className={fieldHeaderClass}>
                <h6 className="mb-0">Date de reception</h6>
              </div>
              <div className={fieldBodyClass}>
                <input
                  type="date"
                  className={inputClass}
                  value={receptionDate}
                  onChange={(e) => setReceptionDate(e.target.value)}
                />
                {errors["receptionDate"] && (
                  <div className="text-danger small mt-1">
                    {errors["receptionDate"]}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm border-dark rounded mb-3">
          <div className="card-header bg-light d-flex align-items-center justify-content-between">
            <h6 className="mb-0">Plans de paiement</h6>
            <button className="btn btn-info btn-sm" onClick={autoDistribute}>
              Auto-répartition
            </button>
          </div>
          <div className="card-body">
            {currentSelected.map((id) => {
              const p = allPlans.find((pl: any) => pl.id === id);
              if (!p) return null;
              const remain = getEditablePlanRemaining(id);
              const alloc =
                allocations.find((a) => a.planId === id)?.amount || 0;
              return (
                <div
                  key={id}
                  className="d-flex align-items-center justify-content-between border border-2 border-secondary-subtle rounded-2 p-2 mb-2 bg-white shadow-sm"
                >
                  <div className="me-3">
                    <div className="fw-semibold text-dark">
                      {p.PaymentPlan.label}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-primary-subtle text-primary">
                        {p.PaymentPlan.due_date}
                      </span>
                      <span className="badge bg-success-subtle text-success">
                        {remain.toLocaleString("fr-FR")} {currency} restant
                      </span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="input-group" style={{ width: 200 }}>
                      <input
                        type="number"
                        className={inputClass}
                        value={alloc}
                        min={0}
                        max={remain}
                        onChange={(e) =>
                          setAllocation(id, parseFloat(e.target.value || "0"))
                        }
                      />
                      <span className="input-group-text">{currency}</span>
                    </div>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removePlan(id)}
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                </div>
              );
            })}
            {errors["allocations"] && (
              <div className="text-danger small mt-1">
                {errors["allocations"]}
              </div>
            )}
            {errors["total"] && (
              <div className="text-danger small mt-1">{errors["total"]}</div>
            )}
          </div>
        </div>

        <div className="card shadow-sm border-dark rounded bg-white">
          <div className="card-body d-flex align-items-center justify-content-between">
            <div className="fw-semibold text-primary">
              Reste:{" "}
              <span className="badge bg-primary-subtle text-primary">
                {remaining.toLocaleString("fr-FR")} {currency}
              </span>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-primary" onClick={validateAndSubmit}>
                Valider
              </button>
              <button className="btn btn-outline-danger" onClick={onClose}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default withPermission(
  EditPaymentModal,
  [buildPermission(Resource.PAYMENT, Action.UPDATE)],
  "masquer",
);
